import React, {useEffect, useState} from "react"
import {Row, Col} from "react-bootstrap"

import {H4} from "@irati/ui/src/Typography"
import {Hr} from "@irati/ui/src/Layout"
import {BasketTotal, Deal} from "@irati/types"
import {FormattedMessage} from "@irati/utils/lang"
import authentication from "@irati/auth"
import currencyService from "@irati/currency/services/currency"
import Loading from "@irati/ui/src/Loading"
import {BasketTotals} from "@irati/deals/utils/deal/calculateTotals"

import discountAndCashback from "@irati/checkout/utils/basket/helper/discountAndCashback"
import Total from "../Total"
import {Subtotal, SelectedEmail, VAT, ProductList} from "../CommonElements"
import {calculateDiscount} from "../helpers/calculateDiscount"
import SavingOptionSelect from "../../Saving/SavingOptionSelect"

interface Props {
  deal: Deal
  email?: string
  walletBalanceToUse?: number
  totals: BasketTotals
  basket: any
  products: any
  isMobile?: boolean
  showSavingOptions?: boolean
}

const OrderSummaryStd: React.FC<Props> = ({deal, email, walletBalanceToUse, totals, basket, products, isMobile, showSavingOptions}) => {
  const [discount, setDiscount] = useState<BasketTotal>()
  const [cashback, setCashback] = useState<BasketTotal>()
  const [flexiPointsCashbackRate, setFlexiPointsCashbackRate] = useState<number>(0)
  const [load, setLoad] = useState<Promise<void> | null>(null)

  useEffect(() => {
    if (deal.earnPoints && !!authentication.apps("wallet.treatTokens") && basket.items.length) {
      const currency = deal.productConfig?.products[0]?.salePrice?.currency!
      const promises = [discountAndCashback(basket.items), currencyService.getFPCashbackForCurrency(currency)]
      setLoad(
        Promise.all(promises).then((data) => {
          const [discountData, fpData] = data
          setDiscount(discountData.discount.total)
          setCashback(discountData.cashback.cashback)
          setFlexiPointsCashbackRate(fpData.fpExchangeRate)
        })
      )
    } else {
      setDiscount(undefined)
      setCashback(undefined)
      setFlexiPointsCashbackRate(0)
    }
  }, [JSON.stringify(basket.items)])

  const {flexiPointsAmount} = calculateDiscount(discount, cashback)
  return (
    <Loading load={load}>
      <Row>
        <Col md={12}>
          <H4 marginTop={3} overflowWrap="break-word">
            {deal.header}
          </H4>
          <Hr />
          {email && deal.emailRequired && <SelectedEmail email={email} />}
          {totals.isOpenValue && totals.subtotal === 0 && <FormattedMessage id="perks.orderSummary.openValue.noAmountEntered" />}
          {totals.hasSelectedAProduct && (
            <ProductList
              data={products}
              breakdown={basket.breakdown}
              displayFlexiPoints={basket.isCashback && !showSavingOptions}
              flexiPointsNumber={flexiPointsAmount}
            />
          )}
          {showSavingOptions && discount && cashback ? (
            <SavingOptionSelect
              deal={deal}
              flexiPointsCashbackRate={flexiPointsCashbackRate}
              discount={discount}
              cashback={cashback}
              isMobile={isMobile}
            />
          ) : (
            <>
              {((!!totals.discount && totals.discount > 0) || (!!walletBalanceToUse && walletBalanceToUse > 0)) && (
                <Subtotal
                  subtotal={totals.subtotal}
                  discount={totals.discount}
                  walletBalanceToUse={walletBalanceToUse}
                  currency={totals.currency}
                />
              )}
              {((!!totals.total && totals.total > 0) || (!!walletBalanceToUse && walletBalanceToUse > 0)) && (
                <div>
                  {totals.vat > 0 && (
                    <Row>
                      <Col xs={12}>
                        <VAT net={totals.total - totals.vat} vat={totals.vat} currency={totals.currency} />
                      </Col>
                    </Row>
                  )}
                  <Total total={totals.total} currency={totals.currency} totalBeforeDiscount={totals.originalPrice} isCashback={basket.isCashback} />
                </div>
              )}
            </>
          )}
        </Col>
      </Row>
    </Loading>
  )
}

OrderSummaryStd.defaultProps = {
  email: "",
  walletBalanceToUse: 0,
  totals: {}
}

export default OrderSummaryStd
